import React, { useState, useMemo } from 'react';
import { Calendar, Plus, BarChart3, List, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  GanttChart,
  ScheduleTaskDrawer,
  ScheduleToolbar,
  LookAheadView,
} from '../components/schedule';
import { EmptyState } from '../components/ui';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useGetScheduleTasksQuery,
  useGetScheduleStatsQuery,
  useCreateScheduleTaskMutation,
  useUpdateScheduleTaskMutation,
  useDeleteScheduleTaskMutation,
  useUpdateScheduleTaskProgressMutation,
  useGetLookAheadQuery,
  useLazyExportScheduleTasksQuery,
} from '../services/scheduleApi';
import type {
  ScheduleTask,
  LookAheadTask,
  ViewMode,
  TaskStatus,
  TaskPriority,
  CreateScheduleTaskRequest,
  UpdateScheduleTaskRequest,
} from '../types/schedule';

type ScheduleViewMode = 'gantt' | 'lookahead' | 'list';

const Schedule: React.FC = () => {
  const { t: _t } = useTranslation();
  const projectId = useCurrentProjectId();

  // UI State
  const [viewMode, setViewMode] = useState<ScheduleViewMode>('gantt');
  const [ganttViewMode, setGanttViewMode] = useState<ViewMode>('week');
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ScheduleTask | null>(null);
  const [lookAheadWeeks, setLookAheadWeeks] = useState<2 | 4>(2);

  // Filter State
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // RTK Query hooks
  const {
    data: tasksResponse,
    isLoading: isLoadingTasks,
    refetch: refetchTasks,
  } = useGetScheduleTasksQuery(
    {
      projectId: projectId || '',
      params: {
        status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
        priority: priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
        search: searchQuery || undefined,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse, isLoading: isLoadingStats } = useGetScheduleStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const { data: lookAheadResponse, isLoading: isLoadingLookAhead } = useGetLookAheadQuery(
    { projectId: projectId || '', weeks: lookAheadWeeks },
    { skip: !projectId || viewMode !== 'lookahead' }
  );

  // Mutations
  const [createTask, { isLoading: isCreating }] = useCreateScheduleTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateScheduleTaskMutation();
  const [_deleteTask] = useDeleteScheduleTaskMutation();
  const [updateProgress] = useUpdateScheduleTaskProgressMutation();
  const [triggerExport] = useLazyExportScheduleTasksQuery();

  // Backend returns { tasks: [], summary: {}, pagination: {} } inside data
  const tasks = tasksResponse?.data?.tasks || [];
  const stats = statsResponse?.data;

  // Filter tasks based on search (client-side for smoother UX)
  const filteredTasks = useMemo(() => {
    if (!searchQuery) return tasks;
    const query = searchQuery.toLowerCase();
    return tasks.filter(
      task =>
        task.name.toLowerCase().includes(query) ||
        task.wbsCode.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query)
    );
  }, [tasks, searchQuery]);

  // Handlers
  const handleCreateTask = () => {
    setEditingTask(null);
    setIsTaskDrawerOpen(true);
  };

  const handleEditTask = (task: ScheduleTask) => {
    setEditingTask(task);
    setIsTaskDrawerOpen(true);
  };

  const handleTaskClick = (task: ScheduleTask) => {
    handleEditTask(task);
  };

  // Handle click from LookAheadView - find full task from tasks array
  const handleLookAheadTaskClick = (lookAheadTask: LookAheadTask) => {
    const fullTask = tasks.find(t => t._id === lookAheadTask._id);
    if (fullTask) {
      handleEditTask(fullTask);
    }
  };

  const handleTaskSubmit = async (data: CreateScheduleTaskRequest | UpdateScheduleTaskRequest) => {
    if (!projectId) return;

    try {
      if (editingTask) {
        await updateTask({
          projectId,
          taskId: editingTask._id,
          data: data as UpdateScheduleTaskRequest,
        }).unwrap();
      } else {
        await createTask({
          projectId,
          data: data as CreateScheduleTaskRequest,
        }).unwrap();
      }
      setIsTaskDrawerOpen(false);
      setEditingTask(null);
    } catch {
      // Error is handled by RTK Query
    }
  };

  const handleDateChange = async (taskId: string, start: Date, end: Date) => {
    if (!projectId) return;

    try {
      await updateTask({
        projectId,
        taskId,
        data: {
          plannedStart: start.toISOString(),
          plannedEnd: end.toISOString(),
        },
      }).unwrap();
    } catch {
      // Error is handled by RTK Query
    }
  };

  const handleProgressChange = async (taskId: string, progress: number) => {
    if (!projectId) return;

    try {
      await updateProgress({
        projectId,
        taskId,
        data: { percentComplete: progress },
      }).unwrap();
    } catch {
      // Error is handled by RTK Query
    }
  };

  const handleExport = async () => {
    if (!projectId) return;

    try {
      const result = await triggerExport({ projectId }).unwrap();
      // Create download link
      const url = window.URL.createObjectURL(result);
      const a = document.createElement('a');
      a.href = url;
      a.download = `schedule-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      // Error handling
    }
  };

  const handleClearFilters = () => {
    setStatusFilter([]);
    setPriorityFilter([]);
  };

  const hasActiveFilters = statusFilter.length > 0 || priorityFilter.length > 0;

  // Compact stat items for inline display
  const statItems = [
    { label: 'Total', value: stats?.totalTasks ?? 0, color: 'text-gray-900' },
    { label: 'In Progress', value: stats?.inProgressTasks ?? 0, color: 'text-blue-600' },
    { label: 'Critical', value: stats?.criticalPathTasks ?? 0, color: 'text-red-600' },
    { label: 'Completed', value: stats?.completedTasks ?? 0, color: 'text-green-600' },
    {
      label: 'Overdue',
      value: stats?.overdueTasks ?? 0,
      color: stats?.overdueTasks ? 'text-red-600' : 'text-gray-400',
    },
  ];

  return (
    <>
      {/* Page Container - Uses calc for proper height within AppLayout */}
      <div className="absolute inset-0 flex flex-col">
        {/* Compact Header Bar - Fixed height */}
        <div className="flex-none bg-white border-b border-gray-200">
          <div className="px-5 py-2.5">
            <div className="flex items-center justify-between">
              {/* Left: Title + Stats */}
              <div className="flex items-center space-x-5">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                    <Calendar className="h-4 w-4 text-brand-600" />
                  </div>
                  <div>
                    <h1 className="text-base font-semibold text-gray-900 leading-tight">
                      Schedule
                    </h1>
                    <p className="text-[11px] text-gray-500 leading-tight">Project timeline</p>
                  </div>
                </div>

                {/* Inline Stats - Compact */}
                <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                  {statItems.map((stat, idx) => (
                    <div key={idx} className="flex items-center space-x-1">
                      <span className={`text-xs font-semibold ${stat.color}`}>{stat.value}</span>
                      <span className="text-[10px] text-gray-400">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center space-x-2">
                {/* View Mode Toggle - Compact */}
                <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                  <button
                    onClick={() => setViewMode('gantt')}
                    className={`flex items-center px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                      viewMode === 'gantt'
                        ? 'bg-white text-brand-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <BarChart3 className="h-3 w-3 mr-1" />
                    Gantt
                  </button>
                  <button
                    onClick={() => setViewMode('lookahead')}
                    className={`flex items-center px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                      viewMode === 'lookahead'
                        ? 'bg-white text-brand-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    Look-Ahead
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                      viewMode === 'list'
                        ? 'bg-white text-brand-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <List className="h-3 w-3 mr-1" />
                    List
                  </button>
                </div>

                <div className="h-5 w-px bg-gray-200" />

                {/* Refresh */}
                <button
                  onClick={() => refetchTasks()}
                  disabled={isLoadingTasks}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isLoadingTasks ? 'animate-spin' : ''}`} />
                </button>

                {/* Primary Action */}
                <button
                  onClick={handleCreateTask}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md transition-colors"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  New Task
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Gantt Toolbar (conditional) - Fixed height */}
        {viewMode === 'gantt' && (
          <div className="flex-none">
            <ScheduleToolbar
              viewMode={ganttViewMode}
              onViewModeChange={setGanttViewMode}
              onShowCriticalPath={setShowCriticalPath}
              showCriticalPath={showCriticalPath}
              onExport={handleExport}
              onRefresh={refetchTasks}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              priorityFilter={priorityFilter}
              onPriorityFilterChange={setPriorityFilter}
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
              isLoading={isLoadingTasks}
            />
          </div>
        )}

        {/* Main Content Area - Fills remaining space */}
        <div className="flex-1 min-h-0 p-4 bg-gray-50">
          <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            {/* Content */}
            {isLoadingTasks ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-brand-600 mx-auto" />
                  <p className="mt-2 text-xs text-gray-500">Loading schedule...</p>
                </div>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <EmptyState
                  icon={Calendar}
                  title="No schedule tasks found"
                  description={
                    searchQuery || hasActiveFilters
                      ? "Try adjusting your filters to find what you're looking for."
                      : 'Get started by creating your first schedule task.'
                  }
                  action={{
                    label: 'Create First Task',
                    onClick: handleCreateTask,
                  }}
                />
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-hidden">
                {viewMode === 'gantt' && (
                  <div className="h-full overflow-auto">
                    <GanttChart
                      tasks={filteredTasks}
                      viewMode={ganttViewMode}
                      onTaskClick={handleTaskClick}
                      onDateChange={handleDateChange}
                      onProgressChange={handleProgressChange}
                      showCriticalPath={showCriticalPath}
                    />
                  </div>
                )}

                {viewMode === 'lookahead' && (
                  <div className="h-full flex flex-col">
                    {/* Look-Ahead Controls - Compact */}
                    <div className="flex-none flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50/50">
                      <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                        {lookAheadWeeks}-Week Look-Ahead
                      </span>
                      <div className="flex items-center space-x-0.5">
                        <button
                          onClick={() => setLookAheadWeeks(2)}
                          className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                            lookAheadWeeks === 2
                              ? 'bg-brand-100 text-brand-700'
                              : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          2W
                        </button>
                        <button
                          onClick={() => setLookAheadWeeks(4)}
                          className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                            lookAheadWeeks === 4
                              ? 'bg-brand-100 text-brand-700'
                              : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          4W
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 min-h-0 overflow-auto">
                      <LookAheadView
                        report={lookAheadResponse?.data}
                        isLoading={isLoadingLookAhead}
                        onTaskClick={handleLookAheadTaskClick}
                        weeks={lookAheadWeeks}
                      />
                    </div>
                  </div>
                )}

                {viewMode === 'list' && (
                  <div className="h-full overflow-auto">
                    <div className="divide-y divide-gray-50">
                      {filteredTasks.map(task => (
                        <div
                          key={task._id}
                          onClick={() => handleTaskClick(task)}
                          className="flex items-center justify-between px-3 py-2 hover:bg-gray-50/50 cursor-pointer transition-colors"
                        >
                          <div className="flex-1 min-w-0 pr-3">
                            <div className="flex items-center space-x-1.5 mb-0.5">
                              <span className="text-[10px] font-mono text-gray-400 bg-gray-50 px-1 py-0.5 rounded">
                                {task.wbsCode}
                              </span>
                              {task.isCritical && (
                                <span className="text-[9px] font-semibold text-red-600 bg-red-50 px-1 py-0.5 rounded uppercase">
                                  Critical
                                </span>
                              )}
                              {task.taskType === 'milestone' && (
                                <span className="text-[9px] font-semibold text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded uppercase">
                                  Milestone
                                </span>
                              )}
                            </div>
                            <h3 className="text-xs font-medium text-gray-900 truncate leading-tight">
                              {task.name}
                            </h3>
                            <div className="flex items-center space-x-2 mt-0.5 text-[10px] text-gray-400">
                              <span>
                                {new Date(task.plannedStart).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                                {' – '}
                                {new Date(task.plannedEnd).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                              <span
                                className={`capitalize ${
                                  task.status === 'completed'
                                    ? 'text-green-600'
                                    : task.status === 'in_progress'
                                      ? 'text-blue-600'
                                      : task.status === 'on_hold'
                                        ? 'text-yellow-600'
                                        : 'text-gray-400'
                                }`}
                              >
                                {task.status.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                          <div className="flex-none text-right">
                            <div className="text-[10px] font-semibold text-gray-600">
                              {task.percentComplete}%
                            </div>
                            <div className="w-16 h-1 bg-gray-100 rounded-full mt-0.5">
                              <div
                                className={`h-1 rounded-full transition-all ${
                                  task.percentComplete === 100
                                    ? 'bg-green-500'
                                    : task.isCritical
                                      ? 'bg-red-500'
                                      : 'bg-brand-500'
                                }`}
                                style={{ width: `${task.percentComplete}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Task Drawer */}
      <ScheduleTaskDrawer
        isOpen={isTaskDrawerOpen}
        onClose={() => {
          setIsTaskDrawerOpen(false);
          setEditingTask(null);
        }}
        task={editingTask}
        tasks={tasks}
        onSubmit={handleTaskSubmit}
        isLoading={isCreating || isUpdating}
      />
    </>
  );
};

export default Schedule;
